import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { connect } from 'react-redux';
import moment from 'moment';
import { useNavigation } from '@react-navigation/native';

import ColorsWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { role } from '@components/frame';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';

import {
  getMachines,
  updateMachineInfo,
  getWallboardEventTimeline,
  setWallboardTimelineEndDate,
  setWallboardSelectedLevel,
  getMachinesGroup,
  setWallboardTimelineStorageNotLoaded,
} from '../../reducers/machines';
import { machineEventDetailsWallboard, getNCFileById, setSelectedNCFile } from '@reducers/manufacture';
import { generateImageURL } from '@reducers/blobStorage';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import { useTrackWallboardOpened } from './hooks/useTrackWallboardOpened';
import useRollWallboardDateAtMidnight from './hooks/useRollWallboardDateAtMidnight';

import {
  machineDetailsConditions,
  formatSinceTime,
  subStatusName,
  subStatuses,
  ncFileStatuses,
  isWeb,
  navigateTo,
  convertTime,
  localTime,
  controlAPICall,
  convertTimeLetter,
  formatTimeEvent,
  isSameDay,
  formatSinceDate,
  removeQueryParam,
  addQueryParams,
} from '../../helpers';

import TouchableElement from '@components/common/touchableElement';
import Arrow from '@components/common/arrow';
import Machines from './components';
import Loading from '@components/loading';
import Svg from '@styles/icons/svg';
import Selector from '@styles/selectors';
import { Shadow } from '@styles/shadow';
import Button from '@styles/buttons';
import AnimatedTable from '@components/settings/components/common/animatedTable';

import { width, height, scaleFactor, notchTop, isChromeOS, is24HourFormat, fullWidth } from '@components/frame';
import { getMachineEventTimelineByBatchId } from '@reducers/machines';
import { productionBatchStatistics, machineStatisticsOeeTeep } from '@reducers/statistics';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { operationsSearch } from '@reducers/search';
import { useForceUpdateEverySecond } from '@v2/pages/common/hooks/useForceUpdateEverySecond';
import { SMART_FACTORY } from '@v2/pages/common/constants/base.constants';

let intervalId;

const MachineList = ({
  isFullScreen,
  getMachines,
  C,
  updateMachineInfo,
  getWallboardEventTimeline,
  generateImageURL,
  getMachineEventTimelineByBatchId,
  productionBatchStatistics,
  offset,
  operationsSearch,
  machines: {
    machines,
    timeOffset,
    machinesLoaded,
    blobAccessInfo,
    minimumTileSize,
    wallboardTimelineStorage,
    wallboardTimelineStorageLoaded,
    wallboardSelectedLevel,
    wallboardTimelineEndDate,
    machinesGroup,
    machinesGroupLoaded,
  },
  sendLog,
  machineEventDetailsWallboard,
  getNCFileById,
  setWallboardTimelineEndDate: setWallboardTimelineEndDateInternal,
  setWallboardSelectedLevel: setWallboardSelectedLevelInternal,
  getMachinesGroup,
  setWallboardTimelineStorageNotLoaded,
  setSelectedNCFile,
  route,
  isWithERP,
}) => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const [isPageReady, setIsPageReady] = useState(false);
  const [filterMachinesByGroup, setFilterMachinesByGroupInternal] = useState(null);
  const [oeeData, setOeeData] = useState({});

  // Route guard: redirect operators to machines
  useEffect(() => {
    if (isRestrictedForOperator(role || '', RESTRICTION_CATEGORIES.WEB_TABS, 'Wallboard')) {
      navigation.replace('Authorized');
    }
  }, [navigation]);

  const setFilterMachinesByGroup = (group) => {
    if (group) {
      addQueryParams({ group });
    } else {
      removeQueryParam('group');
    }
    setFilterMachinesByGroupInternal(group);
  };

  const setWallboardTimelineEndDate = (date) => {
    if (date) {
      addQueryParams({ date: localTime(date).format('YYYY-MM-DD') });
    } else {
      removeQueryParam('date');
    }
    setWallboardTimelineEndDateInternal(date);
  };

  const setWallboardSelectedLevel = (level) => {
    if (level) {
      addQueryParams({ level });
    } else {
      removeQueryParam('level');
    }
    setWallboardSelectedLevelInternal(level);
  };

  useTrackWallboardOpened();
  useRollWallboardDateAtMidnight({ wallboardTimelineEndDate, setWallboardTimelineEndDate });

  const fetchOeeData = async () => {
    const result = await dispatch(
      machineStatisticsOeeTeep({
        date: localTime().format('YYYY-MM-DDTHH:mm:ss'),
        period: 'Day',
        pagination: {
          pageNumber: 1,
          pageSize: 100,
          includeCount: true,
        },
      })
    );
    if (result?.machineStatistics) {
      const mapped = {};
      result.machineStatistics.forEach((item) => {
        mapped[item.machineId] = item;
      });
      setOeeData(mapped);
    }
  };

  useEffect(() => {
    sendLog({ title: LOGGER_TITLES.WALLBOARD_OPEN, status: 'debug' });

    (async () => {
      if (route?.params?.group) {
        await getMachinesGroup();
        setFilterMachinesByGroup(Number(route?.params?.group));
      }

      if (route?.params?.level) {
        setWallboardSelectedLevel(route?.params?.level);
      }
      setWallboardTimelineEndDate(route?.params?.date || localTime().format());
      setIsPageReady(true);

      fetchOeeData(); // fire immediately — doesn't depend on machine list
      await getMachines();
    })();

    let SS = moment().format('ss');
    let oeeCounter = 0;
    intervalId = setInterval(() => {
      if (SS !== moment().format('ss')) {
        SS = moment().format('ss');
        updateMachineInfo();
        oeeCounter++;
        if (oeeCounter >= 12) {
          oeeCounter = 0;
          fetchOeeData();
        }
      }
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  if (!machinesLoaded || !isPageReady) {
    return <Loading />;
  }

  return (
    <Machines
      {...{
        offset,
        nativeID: 'wallboard-container',
        timeOffset,
        machinesLoaded,
        machines,
        font,
        width,
        fullWidth,
        height,
        C,
        minimumTileSize,
        Loading,
        generateImageURL,
        blobAccessInfo,
        navigateBack: () => {},
        navigateTo,
        TouchableElement,
        Svg,
        frameScaleFactor: scaleFactor,
        isAndroid: Platform.OS === 'android',
        isIOS: Platform.OS === 'ios',
        isChromeOS,
        notchTop,
        machineDetailsConditions,
        formatSinceTime,
        subStatusName,
        is24HourFormat,
        subStatuses,
        ncFileStatuses,
        isBackHeader: false,
        isWeb,
        isFullScreen,
        getWallboardEventTimeline,
        convertTime,
        timeLine: wallboardTimelineStorage,
        timeLineLoaded: wallboardTimelineStorageLoaded,
        setTimeLineNotLoaded: setWallboardTimelineStorageNotLoaded,
        wallboardSelectedLevel: wallboardSelectedLevel || 'live',
        localTime,
        Shadow,
        machineEventDetailsWallboard,
        controlAPICall,
        getNCFileById,
        convertTimeLetter,
        formatTimeEvent,
        wallboardTimelineEndDate,
        setWallboardTimelineEndDate,
        sendLog,
        setWallboardSelectedLevel,
        getMachinesGroup,
        machinesGroup,
        machinesGroupLoaded,
        isSameDay,
        Selector,
        Arrow,
        AnimatedTable,
        formatSinceDate,
        setSelectedNCFile,
        filterMachinesByGroup,
        setFilterMachinesByGroup,
        Button,
        getMachineEventTimelineByBatchId,
        productionBatchStatistics,
        operationsSearch,
        useHandleImageNavigation,
        isWithERP,
        useForceUpdateEverySecond,
        navigateTitle: SMART_FACTORY,
        oeeData,
      }}
    />
  );
};

const mapStateToProps = ({
  machines,
  time: {
    server: { offset },
  },
  membership: { isWithERP },
}) => {
  return { machines, offset, isWithERP };
};

export default connect(mapStateToProps, {
  getMachines,
  updateMachineInfo,
  getWallboardEventTimeline,
  generateImageURL,
  sendLog,
  machineEventDetailsWallboard,
  getNCFileById,
  setWallboardSelectedLevel,
  setWallboardTimelineEndDate,
  setWallboardTimelineStorageNotLoaded,
  getMachinesGroup,
  setSelectedNCFile,
  productionBatchStatistics,
  getMachineEventTimelineByBatchId,
  operationsSearch,
})(ColorsWrapper(MachineList));
